/**
 * Permission Service
 *
 * Provides functions to check user permissions based on their Cognito groups.
 * Uses permission mappings defined in permissions.js
 */

import { GROUP_PERMISSIONS, PERMISSIONS } from './permissions';
import { getUserRole } from '../user/cognitoService';

/**
 * Get all permissions for a user based on their groups
 * @param {Object} cognitoUser - Cognito user object with signInUserSession
 * @returns {string[]} Array of permission strings
 */
export const getUserPermissions = (cognitoUser) => {
  if (!cognitoUser) {
    return GROUP_PERMISSIONS.GUEST || [];
  }

  try {
    // Get user's Cognito groups from JWT token
    const groups = cognitoUser.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];

    // Collect all permissions from all groups user belongs to
    const allPermissions = new Set();

    groups.forEach(groupName => {
      const groupPerms = GROUP_PERMISSIONS[groupName] || [];
      groupPerms.forEach(perm => allPermissions.add(perm));
    });

    // If user has no groups but is authenticated, give STANDARD permissions
    if (allPermissions.size === 0 && cognitoUser.username) {
      return GROUP_PERMISSIONS.STANDARD || [];
    }

    return Array.from(allPermissions);
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return GROUP_PERMISSIONS.GUEST || [];
  }
};

/**
 * Check if user has a specific permission
 * @param {Object} cognitoUser - Cognito user object
 * @param {string} permission - Permission to check (from PERMISSIONS constants)
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (cognitoUser, permission) => {
  const userPermissions = getUserPermissions(cognitoUser);
  return userPermissions.includes(permission);
};

/**
 * Check if user has ANY of the specified permissions
 * @param {Object} cognitoUser - Cognito user object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnyPermission = (cognitoUser, permissions) => {
  const userPermissions = getUserPermissions(cognitoUser);
  return permissions.some(perm => userPermissions.includes(perm));
};

/**
 * Check if user has ALL of the specified permissions
 * @param {Object} cognitoUser - Cognito user object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (cognitoUser, permissions) => {
  const userPermissions = getUserPermissions(cognitoUser);
  return permissions.every(perm => userPermissions.includes(perm));
};

// ============================================================================
// SEMANTIC PERMISSION CHECK FUNCTIONS
// These provide clear, readable permission checks for common operations
// ============================================================================

// Search & Match
export const canSearchPersons = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.SEARCH_PERSONS);
export const canFaceMatch = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.FACE_MATCH);

// Person Management
export const canViewPerson = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_PERSON);
export const canEditPerson = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.EDIT_PERSON);
export const canDeletePerson = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.DELETE_PERSON);
export const canImportPersons = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.IMPORT_PERSONS);
export const canExportPersons = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.EXPORT_PERSONS);

// Event Management - Own Events
export const canCreateEvent = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.CREATE_EVENT);
export const canViewOwnEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_OWN_EVENTS);
export const canEditOwnEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.EDIT_OWN_EVENTS);
export const canDeleteOwnEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.DELETE_OWN_EVENTS);

// Event Management - All Events
export const canViewAllEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_ALL_EVENTS);
export const canEditAllEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.EDIT_ALL_EVENTS);
export const canDeleteAllEvents = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.DELETE_ALL_EVENTS);

// Event Management - Assignments
export const canCreateAssignment = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.CREATE_ASSIGNMENT);
export const canCompleteAssignment = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.COMPLETE_ASSIGNMENT);

// User Management
export const canViewUsers = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_USERS);
export const canEditUsers = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.EDIT_USERS);
export const canManageUserGroups = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.MANAGE_USER_GROUPS);
export const canEnableDisableUsers = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.ENABLE_DISABLE_USERS);

// User Management - Combined check for full user admin access
export const canManageUsers = (cognitoUser) => {
  return hasAllPermissions(cognitoUser, [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.MANAGE_USER_GROUPS,
  ]);
};

// System Administration
export const canViewAdminTools = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_ADMIN_TOOLS);
export const canConfigureSystem = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.SYSTEM_CONFIG);
export const canAdminTenant = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.TENANT_ADMIN);
export const canViewSystemLogs = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.VIEW_SYSTEM_LOGS);

// Content Moderation
export const canModerateContent = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.MODERATE_CONTENT);
export const canFlagContent = (cognitoUser) => hasPermission(cognitoUser, PERMISSIONS.FLAG_CONTENT);

/**
 * Check if user can edit a specific event
 * @param {Object} cognitoUser - Cognito user object
 * @param {Object} event - Event object with userId property
 * @returns {boolean} True if user can edit the event
 */
export const canEditEvent = (cognitoUser, event) => {
  if (!cognitoUser || !event) return false;

  // Can edit all events
  if (canEditAllEvents(cognitoUser)) return true;

  // Can edit own events if user owns the event
  if (canEditOwnEvents(cognitoUser)) {
    const userId = cognitoUser.signInUserSession?.accessToken?.payload?.sub;
    return event.userId === userId;
  }

  return false;
};

/**
 * Check if user can delete a specific event
 * @param {Object} cognitoUser - Cognito user object
 * @param {Object} event - Event object with userId property
 * @returns {boolean} True if user can delete the event
 */
export const canDeleteEvent = (cognitoUser, event) => {
  if (!cognitoUser || !event) return false;

  // Can delete all events
  if (canDeleteAllEvents(cognitoUser)) return true;

  // Can delete own events if user owns the event
  if (canDeleteOwnEvents(cognitoUser)) {
    const userId = cognitoUser.signInUserSession?.accessToken?.payload?.sub;
    return event.userId === userId;
  }

  return false;
};

/**
 * Get permission summary for debugging/display
 * @param {Object} cognitoUser - Cognito user object
 * @returns {Object} Summary of user's permissions and role
 */
export const getPermissionSummary = (cognitoUser) => {
  const role = getUserRole(cognitoUser);
  const permissions = getUserPermissions(cognitoUser);
  const groups = cognitoUser?.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];

  return {
    role,
    groups,
    permissions,
    permissionCount: permissions.length,
  };
};
